/* Copyright (c) 2020-present CloudIO Inc */

import { emptyFunction } from '@cloudio-saas/statex';
import { Box } from '@mui/material';
import { useIORef } from 'designer/useIORef';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { BaseProps } from '../componentregistry';
import type { ResizeObserver } from './useComponentSize.type';

declare global {
  interface Window {
    ResizeObserver: typeof ResizeObserver;
  }
}

function getSize(el: HTMLElement | null) {
  if (!el) {
    return {
      width: 0,
      height: 0,
      scrollWidth: 0,
      scrollHeight: 0,
    };
  }

  return {
    width: el.offsetWidth,
    height: el.offsetHeight,
    scrollWidth: el.scrollWidth,
    scrollHeight: el.scrollHeight,
  };
}

export default function useComponentSize() {
  const [width, setWidth] = useState(0);
  const [height, setHeight] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [scrollHeight, setScrollHeight] = useState(0);
  const ioRef = useIORef<HTMLElement>(null);
  const elRef = useRef<HTMLElement | null>(null);
  const el = elRef.current;
  const handleResize = useCallback(() => {
    const {
      width: _w,
      height: _h,
      scrollHeight: _sh,
      scrollWidth: _sw,
    } = getSize(elRef.current);
    setWidth(_w);
    setHeight(_h);
    setScrollWidth(_sw);
    setScrollHeight(_sh);
  }, []);

  useLayoutEffect(() => {
    return ioRef.subscribe((_el) => {
      if (elRef.current !== _el) {
        elRef.current = _el;
        handleResize();
      }
    });
  }, [handleResize, ioRef]);

  useLayoutEffect(() => {
    if (!el) {
      return emptyFunction;
    }

    handleResize();
    if (typeof window.ResizeObserver === 'function') {
      // let frame = 0;
      const resizeObserver = new window.ResizeObserver(() => {
        // resizeObserver.disconnect();
        handleResize();
        // frame = requestAnimationFrame(() => resizeObserver.observe(current));
      });
      resizeObserver.observe(el);

      return () => {
        // if (frame) {
        //   cancelAnimationFrame(frame);
        // }
        resizeObserver.disconnect();
      };
    }
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [el, handleResize]);

  return { width, height, scrollHeight, scrollWidth, ref: ioRef, el };
}

interface Size {
  height: number;
  width: number;
}

export const AutoSizerContext = createContext<Size>({ width: 0, height: 0 });

export function useAutoSizerContext(): Size {
  return useContext(AutoSizerContext);
}

export function WithSizeContext({
  children,
}: {
  children: React.ReactElement | undefined;
}) {
  const s = useComponentSize();
  return (
    <Box
      sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
      ref={s.ref}
    >
      <AutoSizerContext.Provider value={{ width: s.width, height: s.height }}>
        {s.width && s.height ? children : null}
      </AutoSizerContext.Provider>
    </Box>
  );
}

export function WithSize({
  children,
}: {
  children: React.ReactElement | undefined;
}) {
  const s = useComponentSize();
  return (
    <Box
      sx={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 }}
      ref={s.ref}
    >
      {s.width && s.height
        ? React.Children.map<ReactNode, ReactNode>(children, (child) => {
            if (React.isValidElement<BaseProps<never>>(child)) {
              return React.cloneElement(child, {
                width: s.width,
                height: s.height,
              });
            }
            return child;
          })
        : null}
    </Box>
  );
}
